import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ChatAnswer from "./chatAnswer/index";
import ComposeDialog from "src/components/compose/components/dialog";
import { invalidateSeekEmailResponse } from "src/redux/actions/seek";

import styles from "./chat.module.scss";
import cx from "classnames";
import _ from "lodash";
import { parseEmailSubjectAndBody } from "src/utils/seekEmailCompose.helper";
import { selectAsset } from "src/legacy/redux/actions/selection";

interface SeekChatProps {
  sessionView: boolean;
  seekAsset?: boolean;
  seekAnswer?: string | null;
  onAskFollowup?: (question: string) => void;
}

const SeekChat: React.FC<SeekChatProps> = ({
  sessionView,
  seekAsset,
  seekAnswer,
  onAskFollowup
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const seekEmailSuccess = useSelector((state: any) => state?.seek?.email?.success ?? false);
  const seekEmailResponse = useSelector((state: any) => {
    const seekEmailSuccess = state?.seek?.email?.success ?? false;
    if(!seekEmailSuccess){
      return null;
    }

    const body = _.get(state, "seek.email.emailBody");
    if(!body){
      return body;
    }

    const subject = _.get(state, "seek.email.subject");
    const extractedBody = `${body}
        <p>&nbsp;</p>
      <div class="mceNonEditable widget" data-widget-id="assets.grid">
        {{> AssetAttachments}}
      </div>`;

    const assets = _.get(state, "seek.email.assets");

    return {
      subject: subject || "",
      body: extractedBody,
      assets: assets || []
    }
  });

  const profile = useSelector((state: any) => state.users.me);
  const session = useSelector((state: any) => state?.seek?.session);
  const chats = useSelector((state: any) => state?.seek?.session?.item?.chats ?? [],
  );

  const sessionLoading = useSelector((state: any) =>
    _.get(state, "seek.session.loading", false),
  );

  const [completedChats, setCompletedChats] = useState<{
    [key: number]: boolean;
  }>({});

  const [showCompose, setShowCompose] = useState(false);
  
  const seekChats = useMemo(() => {
    const list = chats ?? [];
    if (_.size(list) > 0) {
      return list;
    }
    if (session?.item?.id && !sessionLoading) {
      return [
        {
          uuid: "placeholder",
          session: "",
          question: "",
          answer: "No chats available for this session.",
          citations: [],
          contents: [],
          events: [],
          creating: false,
          helpful: null,
        },
      ];
    }
    return [];
  }, [chats, session?.item?.id, sessionLoading]);

  const handleComplete = useCallback((index: number) => {
    setCompletedChats((prev) => ({ ...prev, [index]: true }));
  }, []); 

  const handleDiscardEmail = () => {
    setShowCompose(false);
    dispatch(invalidateSeekEmailResponse());
  };

  const scrollToBottom = () => {
    if (chatContainerRef && chatContainerRef.current) {
      const scrollTop = _.floor(chatContainerRef.current.scrollTop);
      const scrollHeight = _.floor(chatContainerRef.current.scrollHeight);
      const clientHeight = _.floor(chatContainerRef.current.clientHeight);
      if (scrollTop + clientHeight >= scrollHeight) {
        return;
      }
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (seekAnswer ) {
      const lastIndex = seekChats.length - 1;
      setCompletedChats(prev => ({ ...prev, [lastIndex]: true }));
    }
  }, [seekAnswer, seekChats]);

  useEffect(() => {
    if(!seekEmailSuccess){
      return;
    }

    const hasEmailBody = seekEmailResponse && !_.isEmpty(seekEmailResponse);

    if (hasEmailBody) {
      seekEmailResponse.assets?.forEach((asset: any) => {
        if (asset.settings?.share) dispatch(selectAsset(asset));
      });
    }
    
    setShowCompose(seekEmailSuccess);
  }, [seekEmailSuccess]);

  return (
    <div
      ref={chatContainerRef}
      className={cx(
        styles.chatContainer,
        sessionView && styles.sessionViewChatContainer,
        seekAsset && styles.seekAssetChatContainer,
      )}
    >
      {seekChats.map((chat: any, index: number) => (
        <React.Fragment key={index}>
          <ChatAnswer
            index={index}
            chat={chat}
            profile={profile}
            handleComplete={handleComplete}
            skipTypewriting={!!completedChats[index]}
            showDivider={seekChats.length > 1}
            seekAsset={seekAsset}
            scrollToBottom={scrollToBottom}
            onAskFollowup={onAskFollowup}
            showFollowUps={index === seekChats.length - 1}
          />
        </React.Fragment>
      ))}
      {sessionLoading && _.size(seekChats) === 0 && (
        <span className={styles.loading}>Loading...</span>
      )}

      <ComposeDialog
        show={showCompose}
        dialog={true}
        sourceType="Share Asset"
        initialContent={seekEmailResponse?.assets || []}
        emailBody={seekEmailResponse?.body ?? ""}
        emailSubject={seekEmailResponse?.subject ?? ""}
        onClose={() => handleDiscardEmail()}
        onFinish={() => handleDiscardEmail()}
      />

    </div>
  );
};

export default SeekChat;
